import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Filing } from "../../../../../api/models/filing";
import { FilingService } from "../../../../../api/services/filing.service";
import { MessageService } from "primeng/api";
import { FilingCommunicationService } from "../../sharedServices/filing-communication.service";
import { formatDate } from "@angular/common";
import { TransactionType } from "../../../../../api/models/transaction-type";
import { PlanAction } from "../../../../../api/models/plan-action";
import { handlePasteWithoutLeadingSpaces, preventLeadingSpaceKey } from "src/core/utils/input-validation.utils";

@Component({
  selector: "app-add-data",
  templateUrl: "./add-data.component.html",
  styleUrl: "./add-data.component.css",
  standalone: false,
})
export class AddDataComponent implements OnInit {
  @Input()
  filing: Filing | any = {};
  showMessage = false;
  messageText = "At least one security is required in trading plan.";
  displayModal = true;

  @Output() closeModal = new EventEmitter();
  @Output() filingId: EventEmitter<number> = new EventEmitter();

  first = 0;
  rows = 10;

  transactionTypeOptions = TransactionType.values().map((type) => ({
    label: this.formatTransactionType(type),
    value: type,
  }));

  planActionOptions = PlanAction.values().map((type) => ({
    label: this.formatPlanAction(type),
    value: type,
  }));
   entityTypeOptions = [
    { label: "INDIVIDUAL", value: "INDIVIDUAL" },
    { label: "TRUST", value: "TRUST" },
    { label: "LLC", value: "LLC" },
    { label: "SPOUSE", value: "SPOUSE" },
    { label: "PARTNERSHIP", value: "PARTNERSHIP" },
    { label: "FUND", value: "FUND" },
    { label: "OTHER", value: "OTHER" },
  ];

  formMainGroup: FormGroup;

  constructor(
    private _filingService: FilingService,
    private _messageService: MessageService,
    private filingCommService: FilingCommunicationService,
    private fb: FormBuilder
  ) {
    this.formMainGroup = this.fb.group({
      tradingPlans: this.fb.array([]),
      followUp: [false],
      followUpComment: [""],
    });

    // Add initial row
    this.addRow();
  }

  ngOnInit(): void {}
  formatTransactionType(type: TransactionType): string {
    switch (type) {
      case TransactionType.BUY:
        return "Buy";
      case TransactionType.SELL:
        return "Sell";
      case TransactionType.OTHER:
        return "Other";
      default:
        return type;
    }
  }

  formatPlanAction(type: PlanAction): string {
    switch (type) {
      case "ADOPTION":
        return "Adoption";
      case "TERMINATION":
        return "Termination";
      case "AMENDMENT":
        return "Amendment";
      default:
        return type;
    }
  }

  get tradingPlans(): FormArray {
    return this.formMainGroup.get("tradingPlans") as FormArray;
  }

  getBreakdownControls(tradingPlanIndex: number): FormArray {
    return this.tradingPlans
      .at(tradingPlanIndex)
      .get("securityBreakdowns") as FormArray;
  }

  createTradingPlan(): FormGroup {
    const formGroup = this.fb.group({
      insiderName: ["", Validators.required],
      asReportedReportingEntityName:null,
      asReportedReportingEntityType:null,
      asReportedInsiderRoleInEntity:null,
      planAction: ["", Validators.required],
      relation: ["", Validators.required],
      adoptionDate: null,
      effectiveDate: null,
      terminationDate: null,
      amendmentDate: null,
      expirationDate: null,
      durationDays: ["", [Validators.min(0)]],
      asReportedPlanText: ["", Validators.required],
      securityBreakdowns: this.fb.array([this.createSecurityBreakdown()]),
    });

    // Add conditional validation
    this.setupPlanActionValidation(formGroup);
    return formGroup;
  }
  private setupPlanActionValidation(formGroup: FormGroup) {
    const planActionControl = formGroup.get("planAction");
    const adoptionDateControl = formGroup.get("adoptionDate");
    const terminationDateControl = formGroup.get("terminationDate");
    const amendmentDateControl = formGroup.get("amendmentDate");

    planActionControl?.valueChanges.subscribe((action) => {
      // Clear all validators first
      adoptionDateControl?.clearValidators();
      terminationDateControl?.clearValidators();
      amendmentDateControl?.clearValidators();

      // Set validator based on action
      switch (action) {
        case "ADOPTION":
          adoptionDateControl?.setValidators(Validators.required);
          break;
        case "TERMINATION":
          terminationDateControl?.setValidators(Validators.required);
          break;
        case "AMENDMENT":
          amendmentDateControl?.setValidators(Validators.required);
          break;
      }

      // Update all controls to trigger validation
      adoptionDateControl?.updateValueAndValidity();
      terminationDateControl?.updateValueAndValidity();
      amendmentDateControl?.updateValueAndValidity();
    });

    // Trigger initial validation
    planActionControl?.updateValueAndValidity();
  }

  createSecurityBreakdown(): FormGroup {
    return this.fb.group({
      securityType: ["", Validators.required],
      transactionType: ["", Validators.required],
      numberOfShares: [null, [Validators.min(0)]],
      notionalValue: [null, [Validators.min(0)]],
      sharesDescription: [""],
      tradingConditions: [""],
      earlyTerminationConditions: [""],
    });
  }

  addRow() {
    const newGroup = this.createTradingPlan();
    this.tradingPlans.push(newGroup);
  }

  addSecurityBreakdown(tradingPlanIndex: number) {
    this.showMessage = false;
    this.getBreakdownControls(tradingPlanIndex).push(
      this.createSecurityBreakdown()
    );
  }

  removeSecurityBreakdown(
    tradingPlanIndex: number,
    breakdownIndex: number
  ): void {
    this.getBreakdownControls(tradingPlanIndex).removeAt(breakdownIndex);
  }
  removeTradingPlan(index: number): void {
    this.tradingPlans.removeAt(index);
  }

  isFieldInvalid(tradingPlanIndex: number, fieldName: string): boolean {
    const control = this.tradingPlans.at(tradingPlanIndex).get(fieldName);
    return control
      ? control.invalid && (control.dirty || control.touched)
      : false;
  }

  isBreakdownFieldInvalid(
    tradingPlanIndex: number,
    breakdownIndex: number,
    fieldName: string
  ): boolean {
    const control = this.getBreakdownControls(tradingPlanIndex)
      .at(breakdownIndex)
      .get(fieldName);
    return control
      ? control.invalid && (control.dirty || control.touched)
      : false;
  }

  getErrorMessage(tradingPlanIndex: number, fieldName: string): string {
    const control = this.tradingPlans.at(tradingPlanIndex).get(fieldName);

    if (control?.errors?.["required"]) {
      return "This field is required";
    }
    if (control?.errors?.["min"]) {
      return `Value must be at least ${control.errors["min"].min}`;
    }

    return "";
  }

  getBreakdownErrorMessage(
    tradingPlanIndex: number,
    breakdownIndex: number,
    fieldName: string
  ): string {
    const control = this.getBreakdownControls(tradingPlanIndex)
      .at(breakdownIndex)
      .get(fieldName);

    if (control?.errors?.["required"]) {
      return "This field is required";
    }
    if (control?.errors?.["min"]) {
      return `Value must be at least ${control.errors["min"].min}`;
    }

    return "";
  }

  next() {
    this.first = this.first + this.rows;
  }

  prev() {
    this.first = this.first - this.rows;
  }

  reset() {
    this.first = 0;
  }

  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  isLastPage(): boolean {
    return this.filing ? this.first + this.rows >= this.filing.length : true;
  }

  isFirstPage(): boolean {
    return this.filing ? this.first === 0 : true;
  }

  validateForm() {
    this.markAllFormGroupsAsTouched(this.formMainGroup);

    if (this.formMainGroup.invalid) {
      this._messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Please fill all required fields",
      });
      return;
    }
    // Custom Validation: check if any trading plan has empty securityBreakdowns
    const formValue = this.formMainGroup.value;
    const hasEmptySecurity = formValue.tradingPlans.some(
      (plan: any, index: number) => {
        const breakdowns = plan.securityBreakdowns;
        if (!breakdowns || breakdowns.length === 0) {
          this.showMessage = true;
          return true;
        }
        return false;
      }
    );
    if (hasEmptySecurity) return;
    // const formValue = this.formMainGroup.value;
 
    const filing: any = {
      filingId: this.filing.filingId,
      reason: "Plan Details Created",
      asReportedIssuerId: this.filing.asReportedIssuerId,
      followUpComment: formValue.followUpComment || "No comment",
      tradingPlans: formValue.tradingPlans.map((plan: any) => ({
        planAction: plan.planAction || undefined,
       
        relationshipToIssuer: plan.relation || undefined,
        adoptionDate: this.formatToDateString(plan.adoptionDate) || undefined,
        effectiveDate: this.formatToDateString(plan.effectiveDate) || undefined,
        amendmentDate: this.formatToDateString(plan.amendmentDate) || undefined,
        terminationDate:
          this.formatToDateString(plan.terminationDate) || undefined,
        expirationDate:
          this.formatToDateString(plan.expirationDate) || undefined,
        durationDays: plan.durationDays ? Number(plan.durationDays) : undefined,
        asReportedPlanText: plan.asReportedPlanText || undefined,
        earlyTerminationConditions:
          plan.earlyTerminationConditions || undefined,
        tradingConditions: plan.tradingConditions || undefined,
        asReportedInsider: {
          asReportedInsiderName: plan.insiderName || undefined,
           asReportedReportingEntityName: 
        plan.asReportedReportingEntityName || undefined
      ,
      asReportedReportingEntityType: 
        plan.asReportedReportingEntityType || undefined
      ,
      asReportedInsiderRoleInEntity: 
        plan.asReportedInsiderRoleInEntity || undefined
      ,
        },
        asReportedSecurityBreakdowns: plan.securityBreakdowns.map(
          (sb: any) => ({
            transactionType: sb.transactionType || undefined,
            asReportedSecurity: {
              asReportedSecurityType: sb.securityType || undefined,
            },
            numberOfShares: sb.numberOfShares
              ? Number(sb.numberOfShares)
              : undefined,
            notionalValue: sb.notionalValue
              ? Number(sb.notionalValue)
              : undefined,
            sharesDescription: sb.sharesDescription || undefined,
          })
        ),
      })),
    };

    this._filingService.saveFiling(filing).subscribe({
      next: (response: boolean) => {
        this._messageService.add({
          severity: "success",
          summary: "Success",
          detail: "Filing saved successfully",
        });
      },
      error: (err) => {
        console.error("Error saving filing:", err);
        this._messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to save filing",
        });
      },
      complete: () => {
        this.onCloseModal();
        this.filingCommService.triggerReloadProcessed();
      },
    });
  }

  private markAllFormGroupsAsTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormControl) {
        control.markAsTouched();
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllFormGroupsAsTouched(control);
      }
    });
  }

  onCloseModal() {
    this.closeModal.emit(false);
    this.filingId.emit(this.filing.filingId);
  }
  formatToDateString(date: any): string | null {
    if (!date) return null;
    const d = new Date(date);
    return formatDate(d, "yyyy-MM-dd", "en-US");
  }
    preventSpaceKey(event: KeyboardEvent): void {
    preventLeadingSpaceKey(event);
  }
  // One universal paste handler for ALL fields
 onPasteFormControl(
  event: ClipboardEvent,
  control: AbstractControl | null
): void {
  if (!control) return;

  handlePasteWithoutLeadingSpaces(event, (newValue: string) => {
    control.setValue(newValue);
    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: true });
  });
}

}
